import { useState } from "react";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import {Wifi, Mail, Cloud, Video, Download, Share2} from 'lucide-react';

export default function DigitalCalculator() {
  const [streamingHours, setStreamingHours] = useState("");
  const [emailsSent, setEmailsSent] = useState("");
  const [cloudStorage, setCloudStorage] = useState("");
  const [videoQuality, setVideoQuality] = useState("hd");
  const [socialMediaHours, setSocialMediaHours] = useState("");
  const [emissions, setEmissions] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<{streaming: number; email: number; cloud: number; social: number} | null>(null);

  const calculateEmissions = () => {
    // Streaming emissions based on quality (kg CO2 per hour)
    const qualityFactors: Record<string, number> = {
      "sd": 0.055,
      "hd": 0.106,
      "4k": 0.250,
    };
    const hours = parseFloat(streamingHours) || 0;
    const streamingEmissions = (hours * 365 * qualityFactors[videoQuality]) / 1000;

    // Email emissions (3.5g CO2 per simple email, 26g for email with attachment)
    const emails = parseFloat(emailsSent) || 0;
    const emailEmissions = (emails * 365 * 0.0065) / 1000; // assuming 50% with attachments

    // Cloud storage (0.2 kg CO2 per GB per year)
    const storage = parseFloat(cloudStorage) || 0;
    const cloudEmissions = (storage * 0.2) / 1000;

    // Social media (0.05 kg CO2 per hour - scrolling, loading content)
    const socialHours = parseFloat(socialMediaHours) || 0;
    const socialEmissions = (socialHours * 365 * 0.05) / 1000;

    const total = streamingEmissions + emailEmissions + cloudEmissions + socialEmissions;
    setEmissions(parseFloat(total.toFixed(3)));
    setBreakdown({
      streaming: parseFloat(streamingEmissions.toFixed(3)),
      email: parseFloat(emailEmissions.toFixed(3)),
      cloud: parseFloat(cloudEmissions.toFixed(3)),
      social: parseFloat(socialEmissions.toFixed(3)),
    });
  };

  const shareScore = () => {
    const text = `My digital carbon footprint: ${emissions} tons CO₂e per year. Even emails have a footprint! Calculate yours at www.greenhousegascalculator.com/digital`;
    if (navigator.share) {
      navigator.share({ title: "Digital Carbon Footprint", text });
    } else {
      navigator.clipboard.writeText(text);
      alert("Digital footprint copied to clipboard!");
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Digital Carbon Footprint Calculator",
    "url": "https://www.greenhousegascalculator.com/digital",
    "description": "Calculate your digital carbon footprint from streaming, emails, cloud storage, and social media. Learn how the internet impacts the environment.",
    "applicationCategory": "UtilityApplication",
  };

  return (
    <div className="bg-white">
      <SEO
        title="Digital Carbon Footprint Calculator | Email & Streaming Emissions"
        description="Free digital carbon footprint calculator. Calculate CO2 emissions from streaming, emails, cloud storage, and social media. Your emails have a carbon footprint - measure it here."
        keywords="digital carbon footprint calculator, email carbon footprint, streaming emissions calculator, internet carbon footprint, cloud storage emissions"
        canonical="https://www.greenhousegascalculator.com/digital"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Wifi className="w-16 h-16 text-[#2E8B57]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Your Emails Have a Carbon Footprint - Calculate It Here
            </h1>
            <p className="text-xl text-gray-600">
              Measure the hidden environmental cost of your digital life
            </p>
          </div>
        </div>
      </section>

      {/* Educational Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              The Internet Uses Electricity - And It's Massive
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
              <p className="text-lg font-semibold text-gray-900">
                🌐 If the internet were a country, it would be the <span className="text-blue-600">4th largest energy consumer</span> in the world, behind only China, the USA, and India!
              </p>
            </div>

            <p className="text-gray-700 mb-6">
              Every click, stream, email, and scroll requires electricity to power data centers, servers, networks, and devices. As our digital lives expand, so does our digital carbon footprint - often invisibly.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              The Hidden Carbon Cost of Digital Activities
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-red-50 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-6 h-6 text-red-600" />
                  <h4 className="font-semibold text-gray-900">Emails</h4>
                </div>
                <p className="text-gray-700 mb-2"><strong>4g CO₂</strong> per simple email</p>
                <p className="text-gray-700 mb-2"><strong>50g CO₂</strong> with large attachments</p>
                <p className="text-sm text-gray-600">Sending 65 emails = driving 1 km in a car</p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Video className="w-6 h-6 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">Video Streaming</h4>
                </div>
                <p className="text-gray-700 mb-2"><strong>55g CO₂</strong> per hour (SD)</p>
                <p className="text-gray-700 mb-2"><strong>106g CO₂</strong> per hour (HD)</p>
                <p className="text-sm text-gray-600">One hour of 4K = driving 7 km</p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Cloud className="w-6 h-6 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Cloud Storage</h4>
                </div>
                <p className="text-gray-700 mb-2"><strong>200g CO₂</strong> per GB per year</p>
                <p className="text-sm text-gray-600">1TB of cloud storage = 200kg CO₂ annually</p>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Wifi className="w-6 h-6 text-orange-600" />
                  <h4 className="font-semibold text-gray-900">Social Media</h4>
                </div>
                <p className="text-gray-700 mb-2"><strong>50g CO₂</strong> per hour of scrolling</p>
                <p className="text-sm text-gray-600">Constant content loading uses significant energy</p>
              </div>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              8 Ways to Reduce Your Digital Carbon Footprint
            </h3>

            <div className="space-y-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">1. Unsubscribe from Email Lists</h4>
                <p className="text-gray-700">Every promotional email contributes to emissions. Unsubscribe from newsletters you never read.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">2. Lower Streaming Quality</h4>
                <p className="text-gray-700">Watch in SD instead of 4K when possible. Your phone screen doesn't need 4K resolution!</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">3. Delete Old Emails & Files</h4>
                <p className="text-gray-700">Those thousands of old emails are stored on servers using electricity 24/7. Clean your inbox regularly.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">4. Use Wi-Fi Over Mobile Data</h4>
                <p className="text-gray-700">Wi-Fi is more energy-efficient than cellular data. Connect to Wi-Fi when streaming or downloading.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">5. Download Instead of Stream</h4>
                <p className="text-gray-700">Download music/shows once on Wi-Fi instead of streaming repeatedly. Saves data and emissions!</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">6. Use Dark Mode</h4>
                <p className="text-gray-700">On OLED screens, dark mode reduces energy consumption by up to 60%.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">7. Clean Up Cloud Storage</h4>
                <p className="text-gray-700">Delete duplicate photos, old backups, and files you no longer need. Less storage = less energy.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">8. Block Autoplay Videos</h4>
                <p className="text-gray-700">Disable autoplay on social media and news sites. Watch videos intentionally, not accidentally.</p>
              </div>
            </div>

            <div className="bg-yellow-50 p-6 rounded-lg mt-8">
              <p className="text-gray-700 font-medium">
                ⚡ <strong>Key Insight:</strong> Data centers are becoming greener. Many tech companies now use renewable energy. But the best emission is the one you don't create - reduce unnecessary digital consumption!
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Calculate Your Digital Carbon Footprint
            </h2>

            <div className="space-y-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Video className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Daily Video Streaming (Hours)</label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Hours per day</label>
                    <input
                      type="number"
                      value={streamingHours}
                      onChange={(e) => setStreamingHours(e.target.value)}
                      placeholder="e.g., 2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Typical Quality</label>
                    <select
                      value={videoQuality}
                      onChange={(e) => setVideoQuality(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                    >
                      <option value="sd">SD (Standard Definition)</option>
                      <option value="hd">HD (720p-1080p)</option>
                      <option value="4k">4K (Ultra HD)</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Netflix, YouTube, TikTok, etc.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Emails Sent Per Day</label>
                </div>
                <input
                  type="number"
                  value={emailsSent}
                  onChange={(e) => setEmailsSent(e.target.value)}
                  placeholder="e.g., 20"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Work emails, personal emails, replies
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Cloud className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Cloud Storage Used (GB)</label>
                </div>
                <input
                  type="number"
                  value={cloudStorage}
                  onChange={(e) => setCloudStorage(e.target.value)}
                  placeholder="e.g., 100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Google Drive, iCloud, Dropbox, OneDrive combined
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Wifi className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Social Media Browsing (Hours/Day)</label>
                </div>
                <input
                  type="number"
                  value={socialMediaHours}
                  onChange={(e) => setSocialMediaHours(e.target.value)}
                  placeholder="e.g., 1.5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Instagram, Facebook, Twitter, TikTok, Reddit
                </p>
              </div>
            </div>

            <button
              onClick={calculateEmissions}
              className="w-full bg-[#2E8B57] text-white py-4 rounded-lg font-semibold hover:bg-[#267347] transition-colors"
            >
              Calculate Digital Footprint
            </button>

            {emissions !== null && breakdown && (
              <div className="mt-8 space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-xl border-2 border-[#2E8B57]">
                  <div className="text-center">
                    <p className="text-gray-600 mb-2">Your Annual Digital Carbon Footprint</p>
                    <p className="text-4xl font-bold text-[#2E8B57]">
                      {emissions} tons CO₂e
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {emissions < 0.1 ? "🌟 Excellent! Minimal digital footprint!" : 
                       emissions < 0.3 ? "✅ Good! Below average" :
                       "⚠️ High usage - consider digital reduction tips"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-4">Digital Emissions Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">📺 Video Streaming</span>
                      <span className="font-semibold">{breakdown.streaming} tons ({Math.round((breakdown.streaming/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">📧 Emails</span>
                      <span className="font-semibold">{breakdown.email} tons ({Math.round((breakdown.email/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">☁️ Cloud Storage</span>
                      <span className="font-semibold">{breakdown.cloud} tons ({Math.round((breakdown.cloud/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">📱 Social Media</span>
                      <span className="font-semibold">{breakdown.social} tons ({Math.round((breakdown.social/emissions)*100)}%)</span>
                    </div>
                  </div>
                </div>

                <EquivalenciesVisualizer emissions={emissions} />

                <div className="flex gap-4">
                  <button
                    onClick={() => exportToPDF({
                      title: "Digital Carbon Footprint Report",
                      totalEmissions: emissions,
                      breakdown,
                      calculationType: "digital"
                    })}
                    className="flex-1 bg-[#2E8B57] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#267347] transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Export Report
                  </button>
                  <button
                    onClick={shareScore}
                    className="flex-1 bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Share Your Score
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
